import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import toast from 'react-hot-toast';

import AuthService from '@/services/auth/auth-service';
import Request from '@/services/util/request-service';

// Simple in-memory guards to prevent rapid, overlapping validations
let isValidating = false;
let lastValidationAt = 0;
let consecutiveFailures = 0;
const MIN_INTERVAL_MS = 5000; // do not validate more than once every 5s

const sessionValidationCheck = async (
  router: AppRouterInstance,
  reduxCallback: () => void,
  heartbeatCallback?: () => void,
) => {
  try {
    // Throttle and single-flight guard
    const now = Date.now();
    if (isValidating || now - lastValidationAt < MIN_INTERVAL_MS) return;
    isValidating = true;

    const req = await Request.get('auth/session/validate');
    // If the transport itself failed, treat as transient failure
    if (!req) {
      throw new Error('Session validation request failed');
    }
    const response = await req.json();

    // If the session is invalid, trigger logout
    if (!response || response.error || !response.valid) {
      toast.error('Session is no longer valid');
      reduxCallback();
      AuthService.signOutReset();
      if (heartbeatCallback) heartbeatCallback();
      router.push('/');
      consecutiveFailures = 0;
    }
    // Successful validation
    lastValidationAt = Date.now();
    consecutiveFailures = 0;
    isValidating = false;
  } catch (error) {
    // On transient network errors, avoid immediate logout unless repeated
    consecutiveFailures += 1;
    // Only force logout after a few consecutive failures to avoid false positives
    if (consecutiveFailures >= 3) {
      toast.error('Error while validating session');
      reduxCallback();
      AuthService.signOutReset();
      if (heartbeatCallback) heartbeatCallback();
      router.push('/');
      consecutiveFailures = 0;
    }
    lastValidationAt = Date.now();
    isValidating = false;
  }
};

const SessionValidationService = {
  // Default interval is 5 minutes
  startHeartbeat(router: AppRouterInstance, reduxCallback: () => void, interval = 300000) {
    // Call validation once on heartbeat start (respects throttle)
    sessionValidationCheck(router, reduxCallback);

    // Re-validate on after the set interval
    const heartbeat = setInterval(async () => {
      sessionValidationCheck(router, reduxCallback, () => clearInterval(heartbeat));
    }, interval);

    return heartbeat;
  },
};

export default SessionValidationService;
